import { BasicMantineTable } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTable';
import { TooltipButton } from '@dealstack/frontend/shared/components/Table/MantineTable/TooltipButton/TooltipButton';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Badge, Box, Button, Flex, Text } from '@mantine/core';
import {
	useTranslationMipManage,
	useTranslationMipCreate,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useAppSelector } from '@dealstack/frontend/app/hooks/redux';
import { ProgrammeSettingsView } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MipManage.types';
import {
	ProgrammeUserWithPermissionRolesRes,
	ResourceType,
	PermissionRoleWithOwnerEnum,
} from '@dealstack/shared/lib/generated';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { getResourceTypePermissions } from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import {
	createUserNameColumn,
	createUserPermissionsColumn,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/UserColumn';

interface Props {
	users?: ProgrammeUserWithPermissionRolesRes[];
	isLoading: boolean;
	onEditClick?: (id: string) => void;
	onResendInvitationClick: (
		firstName: string,
		lastName: string,
		email: string,
		isContactPerson: boolean
	) => void;
	onRemoveUserClick: (id: string) => void;
	onAddUserClick: () => void;
	onViewChange: (view: ProgrammeSettingsView, userId: string) => void;
}

export const Table = ({
	users,
	isLoading,
	onEditClick,
	onResendInvitationClick,
	onRemoveUserClick,
	onAddUserClick,
	onViewChange,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const tMipCreate = useTranslationMipCreate();

	const userState = useAppSelector((state) => state.user);
	const currentUserId = userState.data?.id;
	const programmePermissions = [
		...getResourceTypePermissions(ResourceType.PROGRAMME),
		PermissionRoleWithOwnerEnum.OWNER,
	];

	const permissions = programmePermissions
		.map((relation) => ({
			label: tMipCreate(`invite_team_member.relation.${relation}`),
			value: relation,
		}))
		// sort by alphabetical order
		.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

	const columns: ExtendedColumnDef<ProgrammeUserWithPermissionRolesRes>[] = [
		createUserNameColumn<ProgrammeUserWithPermissionRolesRes>({
			header: tMipManage('root.programme_members.table.column.name'),
			filterPlaceholder: tMipManage('root.programme_members.table.filter.name'),
			filterText: tMipManage('root.programme_members.table.filter.name'),
		}),
		createUserPermissionsColumn<ProgrammeUserWithPermissionRolesRes>({
			id: 'permissions',
			t: tMipCreate,
			header: tMipManage('root.programme_members.table.column.permissions'),
			filterPlaceholder: tMipManage('root.programme_members.table.filter.permissions'),
			filterText: tMipManage('root.programme_members.table.filter.permissions'),
			permissions,
		}),
		{
			cell: ({ row }) => {
				return (
					<Badge
						c={row.original.isContactPerson ? 'blue.6' : 'dark.4'}
						bg={row.original.isContactPerson ? 'blue.0' : 'transparent'}
						fw={400}
						sx={(theme) => ({
							textTransform: 'uppercase',
							border: row.original.isContactPerson
								? 'none'
								: `1px solid ${theme.colors.gray[3]}`,
						})}
					>
						{row.original.isContactPerson
							? tMipManage('root.programme_members.contact.yes')
							: tMipManage('root.programme_members.contact.no')}
					</Badge>
				);
			},
			header: tMipManage('root.programme_members.table.column.contact_person'),
			id: 'isContactPerson',
			enableGlobalFilter: false,
		},
		{
			cell: ({ row }) => {
				return (
					<Flex w="100%" align="center" justify="flex-start" wrap="nowrap">
						{onEditClick && (
							<Button
								variant="subtle"
								style={{ flexShrink: 0 }}
								onClick={(e) => {
									e.stopPropagation();
									onEditClick(row.original.id);
								}}
							>
								{tMipManage('root.programme_members.button.edit')}
							</Button>
						)}
						{!row.original.active && (
							<Button
								variant="subtle"
								style={{ flexShrink: 0 }}
								onClick={(e) => {
									e.stopPropagation();
									onResendInvitationClick(
										row.original.firstName,
										row.original.lastName,
										row.original.email,
										row.original.isContactPerson
									);
								}}
							>
								{tMipManage('root.programme_members.button.resend')}
							</Button>
						)}
						{currentUserId !== row.original.id && row.original.canBeRemoved && (
							<Button
								variant="subtle"
								color="red"
								style={{ flexShrink: 0 }}
								onClick={(e) => {
									e.stopPropagation();
									onRemoveUserClick(row.original.id);
								}}
							>
								{tMipManage('root.programme_members.button.remove')}
							</Button>
						)}
					</Flex>
				);
			},
			header: '',
			id: 'actions',
			size: 300,
			minSize: 300,
		},
	];

	if (!users?.length) {
		return (
			<Box
				p="5rem"
				ta="center"
				sx={(theme) => ({
					border: '1px dashed',
					borderColor: theme.colors.gray[3],
					borderRadius: '8px',
				})}
			>
				<Text c="dimmed" ta="center">
					{tMipManage('root.programme_members.table.no_members')}
				</Text>
				<Button onClick={onAddUserClick} mt="md">
					{tMipManage('root.programme_members.button.invite')}
				</Button>
			</Box>
		);
	}

	return (
		<BasicMantineTable<ProgrammeUserWithPermissionRolesRes>
			columns={columns}
			isLoading={isLoading}
			data={users}
			mantineTableBodyRowProps={({ row }) => ({
				onClick: () => {
					onViewChange(ProgrammeSettingsView.User, row.original.id);
				},
				style: { cursor: 'pointer' },
				sx: (theme) => ({
					'&:hover': {
						backgroundColor: theme.colors.gray[0],
					},
				}),
			})}
			customActions={
				<TooltipButton
					leftSection={<Icon variant="plus" size="vlg" />}
					text={tMipManage('root.programme_members.button.add_member')}
					onClick={onAddUserClick}
				/>
			}
		/>
	);
};
